/**
 * Signatures service
 * Service for fetching email signatures from the API
 */
import type {
  EmployeeSignatureListRequest,
  EmployeeSignatureListResponse,
  SignatureEmailsRequest,
  SignatureEmailsResponse,
  SignatureEmailsErrorResponse,
} from '../Components/SignatureList/Signatures.types';
import { authenticatedFetch } from '@/Auth/services/auth.service';
import { env } from '@/config/env';

const SIGNATURES_ENDPOINT = '/v1/email/signature-emails';
const SEND_SIGNATURE_ENDPOINT = '/v1/email/send-signature';

/**
 * Request payload for sending signature
 */
export interface SendSignatureRequest {
  email: string;
  nome: string;
  phone: string;
  position: string;
  image: string; // base64 encoded image
  buCategory: string;
}

/**
 * Success response from send signature
 */
export interface SendSignatureResponse {
  success: boolean;
  message: string;
}

/**
 * Error response from send signature
 */
export interface SendSignatureErrorResponse {
  statusCode: number;
  message: string | string[];
  error: string;
}

/**
 * Get signature emails with filters and pagination
 */
export const getSignatureEmails = async (
  params: SignatureEmailsRequest = {},
): Promise<SignatureEmailsResponse> => {
  try {
    // Build query string from params
    const queryParams = new URLSearchParams();

    if (params.email) queryParams.append('email', params.email);
    if (params.recipientName) queryParams.append('recipientName', params.recipientName);
    if (params.buCategory) queryParams.append('buCategory', params.buCategory);
    if (params.createdById) queryParams.append('createdById', params.createdById);
    if (params.fromDate) queryParams.append('fromDate', params.fromDate);
    if (params.toDate) queryParams.append('toDate', params.toDate);
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());

    const queryString = queryParams.toString();
    const url = `${env.bffBaseURL}${SIGNATURES_ENDPOINT}${queryString ? `?${queryString}` : ''}`;

    const response = await authenticatedFetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData: SignatureEmailsErrorResponse = await response.json().catch(() => ({
        message: 'Failed to fetch signature emails',
        statusCode: response.status,
      }));

      throw new Error(errorData.message || 'Failed to fetch signature emails');
    }

    const data: SignatureEmailsResponse = await response.json();
    return data;
  } catch (error: unknown) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('An unexpected error occurred');
  }
};

/**
 * Send signature via email
 */
export const sendSignature = async (
  payload: SendSignatureRequest,
): Promise<SendSignatureResponse> => {
  try {
    const url = `${env.bffBaseURL}${SEND_SIGNATURE_ENDPOINT}`;

    const { buCategory, ...body } = payload;

    const response = await authenticatedFetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Bu-Name': buCategory,
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const errorData: SendSignatureErrorResponse = await response.json().catch(() => ({
        statusCode: response.status,
        message: 'Failed to send signature',
        error: 'Unknown error',
      }));

      // Handle different error status codes
      if (response.status === 400) {
        const messages = Array.isArray(errorData.message)
          ? errorData.message.join(', ')
          : errorData.message;
        throw new Error(`Invalid request: ${messages}`);
      } else if (response.status === 403) {
        throw new Error('Insufficient permissions to send signature');
      } else {
        throw new Error(
          Array.isArray(errorData.message)
            ? errorData.message.join(', ')
            : errorData.message || 'Failed to send signature',
        );
      }
    }

    const data: SendSignatureResponse = await response.json();
    return data;
  } catch (error: unknown) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('An unexpected error occurred while sending signature');
  }
};

const EMPLOYEE_SIGNATURES_ENDPOINT = '/v1/email/signatures';

/**
 * Signatures generated per tenant.
 *
 * Unlike {@link getSignatureEmails}, which lists send events, this lists the signature records
 * themselves with their status. An unknown or deactivated `tenantSlug` comes back as an empty
 * page rather than a 404.
 */
export const getEmployeeSignatures = async (
  params: EmployeeSignatureListRequest = {},
): Promise<EmployeeSignatureListResponse> => {
  const queryParams = new URLSearchParams();

  if (params.tenantSlug) queryParams.append('tenantSlug', params.tenantSlug);
  if (params.status) queryParams.append('status', params.status);
  if (params.search) queryParams.append('search', params.search);
  if (params.includeDeleted) queryParams.append('includeDeleted', 'true');
  if (params.page) queryParams.append('page', String(params.page));
  if (params.limit) queryParams.append('limit', String(params.limit));

  const queryString = queryParams.toString();
  const url = `${env.bffBaseURL}${EMPLOYEE_SIGNATURES_ENDPOINT}${queryString ? `?${queryString}` : ''}`;

  const response = await authenticatedFetch(url, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });

  if (!response.ok) {
    const errorData: SignatureEmailsErrorResponse = await response.json().catch(() => ({
      message: 'Failed to fetch signatures',
      statusCode: response.status,
    }));

    throw new Error(errorData.message || 'Failed to fetch signatures');
  }

  return (await response.json()) as EmployeeSignatureListResponse;
};

/**
 * Removes a signature from the listing.
 *
 * Not a hard delete: the BFF keeps the row, because the delivery record cascades from it.
 * `restoreSignature` reverses it.
 */
export const deleteSignature = async (id: string): Promise<void> => {
  const response = await authenticatedFetch(
    `${env.bffBaseURL}${EMPLOYEE_SIGNATURES_ENDPOINT}/${id}`,
    { method: 'DELETE', headers: { 'Content-Type': 'application/json' } },
  );

  if (!response.ok) {
    if (response.status === 403) throw new Error('Você não tem permissão para excluir assinaturas');
    if (response.status === 404) throw new Error('Assinatura não encontrada');
    throw new Error('Não foi possível excluir a assinatura');
  }
};

/** Puts a removed signature back in the listing. */
export const restoreSignature = async (id: string): Promise<void> => {
  const response = await authenticatedFetch(
    `${env.bffBaseURL}${EMPLOYEE_SIGNATURES_ENDPOINT}/${id}/restore`,
    { method: 'PATCH', headers: { 'Content-Type': 'application/json' } },
  );

  if (!response.ok) {
    if (response.status === 403)
      throw new Error('Você não tem permissão para restaurar assinaturas');
    if (response.status === 404) throw new Error('Assinatura não encontrada');
    throw new Error('Não foi possível restaurar a assinatura');
  }
};

/**
 * Sends an existing signature again, to the address already on the record.
 *
 * The image travels in the body because the BFF never stored it — see
 * `renderSignatureBase64`. Rate-limited to one per minute on the server.
 */
export const resendSignature = async (id: string, image: string): Promise<void> => {
  const response = await authenticatedFetch(
    `${env.bffBaseURL}${EMPLOYEE_SIGNATURES_ENDPOINT}/${id}/resend`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ image }),
    },
  );

  if (!response.ok) {
    if (response.status === 403)
      throw new Error('Você não tem permissão para reenviar assinaturas');
    if (response.status === 404) throw new Error('Assinatura não encontrada');
    if (response.status === 422) {
      throw new Error('Esta BU não tem assinatura configurada');
    }
    if (response.status === 429) {
      throw new Error('Aguarde um minuto antes de reenviar outra assinatura');
    }
    if (response.status === 503) {
      throw new Error('O serviço de e-mail não está configurado neste ambiente');
    }
    throw new Error('Não foi possível reenviar a assinatura');
  }
};
